/**
 * The munging pipeline.
 *
 * Fetch master → derive angles/qualities/tracks → extract the requested angle →
 * cap quality → rewrite + serve the media playlists → serve the master → hand
 * the result to the adapter. EVERY source takes that path, encrypted or not,
 * narrowed or not: the media playlists it reads on the way are the same ones
 * the engine was about to fetch anyway, and having them in hand is what lets
 * the wrapper reason about the output (chunk boundaries for prefetch, key
 * requirements, absolutized segment URIs) instead of guessing.
 */
import { extractAnglePlaylist, isEncryptedPayload, listAngles, } from '@luminary-media-converter/hls-core';
import { AUDIO_ONLY_ANGLE_ID, } from '../types.js';
import { collapseAudioGroups } from './audio-groups.js';
import { buildAudioOnlyMaster, canRenderAudioOnly, isAudioOnlyMaster, } from './audio-only.js';
import { KEY_CONTENT_TYPE, PLAYLIST_CONTENT_TYPE, VTT_CONTENT_TYPE, } from './content-types.js';
import { keyBytes } from './decrypt.js';
import { PipelineError, decodeMaybeEncrypted, fetchBytes, fetchMaybeEncrypted, } from './fetch.js';
import { scanMediaPlaylist } from './media-scan.js';
import { absolutize, absolutizeAgainst, anchorToDocument, collectMasterRefs, isMasterModel, masterHasVideo, parseMasterText, substituteMasterRefs, } from './playlist-text.js';
import { applyQualityCap, listQualities } from './quality-cap.js';
import { LUMINARY_KEY_PLACEHOLDER_URI, rewriteMediaPlaylist, } from './rewrite-media.js';
/** Angle id used when the master declares no `TYPE=VIDEO` rendition groups. */
export const DEFAULT_ANGLE_ID = 'default';
// ---------------------------------------------------------------------------
// Step 1–2: fetch + derive
// ---------------------------------------------------------------------------
export async function loadMaster(url, ctx) {
    // Everything read from here on resolves against this URL, so it is made
    // absolute once rather than on every URI that needs it.
    const absolute = anchorToDocument(url);
    const asset = await fetchMaybeEncrypted(absolute, {
        fetchImpl: ctx.fetchImpl,
        keyHex: ctx.keyHex,
        expect: 'playlist',
        signal: ctx.signal,
        // Checked with the server on every load: a master is rewritten in
        // place when angles or tracks change, and one stored without a
        // Cache-Control header would otherwise be served stale from the HTTP
        // cache for as long as the browser guesses it fresh. It is small, and
        // an unchanged one costs a 304.
        httpCache: 'no-cache',
        subtle: ctx.subtle,
    });
    ctx.cache.set(absolute, asset.text);
    return describeMaster(absolute, asset.text, asset.wasEncrypted);
}
/** Pure derivation half of {@link loadMaster}; exported for fixture specs. */
export function describeMaster(url, served, wasEncrypted = false) {
    // Every engine plays what is derived from here, so a language served twice
    // (a stereo and a mono group) is offered once on all of them.
    const text = collapseAudioGroups(served);
    // Parsed once, and every question below asked of the one model. It was
    // five parses, one of them building a whole audio-only master only to
    // compare it with null.
    const parsed = parseMasterText(text);
    const { master } = parsed;
    if (!isMasterModel(master)) {
        return {
            url,
            text,
            wasEncrypted,
            isMaster: false,
            angles: [],
            audioTracks: [],
            subtitleTracks: [],
            nativelyAudioOnly: false,
        };
    }
    const nativelyAudioOnly = !masterHasVideo(master);
    const videoAngles = listAngles(master).map((angle) => ({
        id: angle.id,
        name: angle.name,
        isDefault: angle.isDefault,
    }));
    const angles = [...videoAngles];
    if (!nativelyAudioOnly && angles.length === 0) {
        angles.push({ id: DEFAULT_ANGLE_ID, name: 'Default', isDefault: true });
    }
    if (!nativelyAudioOnly && angles.length > 0 && canRenderAudioOnly(master)) {
        angles.push({
            id: AUDIO_ONLY_ANGLE_ID,
            name: 'Audio only',
            isDefault: false,
        });
    }
    const audioTracks = [];
    const subtitleTracks = [];
    for (const entry of parsed.media) {
        if (entry.type === 'AUDIO') {
            audioTracks.push({
                id: masterTrackId('a', entry.groupId, entry.name, entry.language),
                lang: entry.language,
                label: entry.name ?? entry.language ?? 'Audio',
            });
        }
        else if (entry.type === 'SUBTITLES') {
            subtitleTracks.push({
                id: masterTrackId('m', entry.groupId, entry.name, entry.language),
                lang: entry.language,
                label: entry.name ?? entry.language ?? 'Subtitles',
                source: 'master',
            });
        }
    }
    return {
        url,
        text,
        wasEncrypted,
        isMaster: true,
        angles,
        audioTracks: dedupeByName(dedupeById(audioTracks)),
        subtitleTracks: dedupeById(subtitleTracks),
        nativelyAudioOnly,
    };
}
// ---------------------------------------------------------------------------
// Step 3–6: narrow, cap, rewrite, serve
// ---------------------------------------------------------------------------
export async function mungeSource(info, options, ctx) {
    const narrowedText = narrow(info, options.angleId);
    const offered = applyQualityCap(narrowedText, options.maxHeight);
    const qualities = info.isMaster ? listQualities(offered) : [];
    const capped = applyQualityCap(offered, options.selectHeight);
    const isAudioOnly = options.angleId === AUDIO_ONLY_ANGLE_ID ||
        info.nativelyAudioOnly ||
        (info.isMaster && isAudioOnlyMaster(capped));
    let keyUri = ctx.served?.keyUri;
    const resolveKeyUri = () => {
        if (!ctx.keyHex)
            return undefined;
        if (keyUri)
            return keyUri;
        keyUri =
            ctx.keyDelivery === 'memory'
                ? LUMINARY_KEY_PLACEHOLDER_URI
                : ctx.serveStrategy.serve(keyBytes(ctx.keyHex), KEY_CONTENT_TYPE);
        if (ctx.served)
            ctx.served.keyUri = keyUri;
        return keyUri;
    };
    const mediaPlaylists = [];
    let isLive = false;
    /**
     * Hand a live playlist to the serving layer, which owns keeping it fresh.
     *
     * Returns the URL to serve it from, or throws when the strategy cannot do
     * the job. Refusing is the point: served statically, a live playlist would
     * play its first snapshot and then sit at the end of it forever, which
     * looks like playback and is not.
     */
    const serveLivePlaylist = (url, scan) => {
        const spec = {
            url,
            baseUrl: url,
            ...(ctx.keyHex
                ? { keyUri: resolveKeyUri(), keyBytes: keyBytes(ctx.keyHex) }
                : {}),
            refreshSec: scan.targetDurationSec,
        };
        const served = ctx.serveStrategy.serveLive?.(spec);
        if (!served) {
            throw new PipelineError('live-unsupported', `${url} is still being written (no #EXT-X-ENDLIST) and this ` +
                'player cannot refresh a live playlist', { url });
        }
        return served;
    };
    let servedMasterText;
    let url;
    if (!info.isMaster) {
        const memo = ctx.served?.playlists.get(info.url);
        // One read of the playlist answers every question asked of it below.
        const scan = memo?.scan ?? scanMediaPlaylist(capped);
        requireKeyFor(scan, info.url, ctx);
        // The URL served a media playlist directly — it IS the only stream, so
        // it counts as video for anything reading the list back.
        mediaPlaylists.push({
            url: info.url,
            mediaType: 'VIDEO',
            text: capped,
            scan,
        });
        if (scan.isLive) {
            isLive = true;
            // Nothing to serve as a master: the live URL IS the source.
            const liveUrl = memo?.url ?? serveLivePlaylist(info.url, scan);
            ctx.served?.playlists.set(info.url, { url: liveUrl, scan });
            return {
                source: {
                    url: liveUrl,
                    isBlob: true,
                    ...(ctx.keyHex && ctx.keyDelivery === 'memory'
                        ? { keyHex: ctx.keyHex }
                        : {}),
                },
                qualities,
                isAudioOnly,
                masterText: capped,
                mediaPlaylists,
                isLive,
            };
        }
        // Rewritten even when already served: the text is part of the result,
        // and without the per-segment URL parses it costs next to nothing.
        servedMasterText = rewriteMediaPlaylist(capped, {
            playlistUrl: info.url,
            keyUri: scan.hasAes128Key ? resolveKeyUri() : undefined,
        });
        url =
            memo?.url ??
                ctx.serveStrategy.serve(servedMasterText, PLAYLIST_CONTENT_TYPE);
        ctx.served?.playlists.set(info.url, { url, scan });
    }
    else {
        const replacements = new Map();
        const refs = collectMasterRefs(capped).map((ref) => ({
            ref,
            absolute: absolutize(ref.uri, info.url),
        }));
        // Every read starts here, at once; the loop consumes them in master
        // order. None depends on another, and reading them one after another
        // put a round trip per playlist in front of playback — twenty on a
        // multi-language live ladder. Consuming in order keeps everything
        // observable as it was: which failure is reported when several reads
        // fail, the order of `mediaPlaylists`, and the order the strategy is
        // asked to serve in.
        const reads = startPlaylistReads(refs.map(({ absolute }) => absolute), ctx);
        for (const { ref, absolute } of refs) {
            const text = await reads.get(absolute);
            const memo = ctx.served?.playlists.get(absolute);
            // One read of the playlist answers every question asked of it
            // below, and travels on to the chunk schedules with it.
            const scan = memo?.scan ?? scanMediaPlaylist(text);
            requireKeyFor(scan, absolute, ctx);
            mediaPlaylists.push({
                url: absolute,
                mediaType: ref.mediaType ?? 'VIDEO',
                text,
                scan,
            });
            if (scan.isLive)
                isLive = true;
            // Served for this source already, and nothing that differs between
            // munges of it changes what serving it again would produce.
            if (memo) {
                replacements.set(ref.uri, memo.url);
                continue;
            }
            let served;
            if (scan.isLive) {
                served = serveLivePlaylist(absolute, scan);
            }
            else {
                const segmentReplacements = ref.mediaType === 'SUBTITLES' && ctx.keyHex
                    ? await serveDecryptedVttSegments(scan, absolute, ctx)
                    : undefined;
                const rewritten = rewriteMediaPlaylist(text, {
                    playlistUrl: absolute,
                    keyUri: scan.hasAes128Key ? resolveKeyUri() : undefined,
                    segmentReplacements,
                });
                served = ctx.serveStrategy.serve(rewritten, PLAYLIST_CONTENT_TYPE);
            }
            ctx.served?.playlists.set(absolute, { url: served, scan });
            replacements.set(ref.uri, served);
        }
        servedMasterText = substituteMasterRefs(capped, replacements);
        url = ctx.serveStrategy.serve(servedMasterText, PLAYLIST_CONTENT_TYPE);
    }
    return {
        source: {
            url,
            isBlob: true,
            ...(ctx.keyHex && ctx.keyDelivery === 'memory'
                ? { keyHex: ctx.keyHex }
                : {}),
        },
        qualities,
        isAudioOnly,
        masterText: servedMasterText,
        mediaPlaylists,
        isLive,
    };
}
// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
function narrow(info, angleId) {
    if (!info.isMaster)
        return info.text;
    if (angleId === AUDIO_ONLY_ANGLE_ID) {
        return buildAudioOnlyMaster(info.text) ?? info.text;
    }
    if (!angleId || angleId === DEFAULT_ANGLE_ID)
        return info.text;
    return extractAnglePlaylist(info.text, angleId);
}
async function fetchPlaylistText(url, ctx) {
    const cached = ctx.cache.get(url);
    if (cached !== undefined)
        return cached;
    const asset = await fetchMaybeEncrypted(url, {
        fetchImpl: ctx.fetchImpl,
        keyHex: ctx.keyHex,
        expect: 'playlist',
        signal: ctx.signal,
        subtle: ctx.subtle,
    });
    ctx.cache.set(url, asset.text);
    return asset.text;
}
/**
 * Start reading every URL at once, one read per distinct URL, and return the
 * pending reads by URL for the caller to await in the order it needs.
 *
 * Each read is marked handled as it starts. The caller awaits them one at a
 * time, so a read that fails before its turn would otherwise surface as an
 * unhandled rejection — and once an earlier read has thrown, its turn never
 * comes at all.
 */
function startPlaylistReads(urls, ctx) {
    const reads = new Map();
    for (const url of urls) {
        if (reads.has(url))
            continue;
        const read = fetchPlaylistText(url, ctx);
        void read.catch(() => undefined);
        reads.set(url, read);
    }
    return reads;
}
/**
 * Subtitle segments read at once: enough to overlap the round trips, few enough
 * to leave the connections the engine is about to need.
 */
const VTT_READS_IN_FLIGHT = 4;
/**
 * The engine fetches a SUBTITLES playlist's `.vtt` segments itself and cannot
 * decrypt LMCENC, so encrypted ones are decrypted here and served as plaintext.
 * Plaintext ones are left alone (the rewrite absolutizes them). VOD only —
 * the segment list is finite.
 *
 * Each distinct segment is read once, a few at a time: read one after another,
 * a segmented track put a round trip per segment in front of playback. They
 * are consumed in playlist order, as the media playlists are, so the failure
 * reported and the order the strategy is asked to serve in are as they were.
 */
async function serveDecryptedVttSegments(playlist, playlistUrl, ctx) {
    const resolve = absolutizeAgainst(playlistUrl);
    const urls = [...new Set(playlist.runs.map((run) => resolve(run.uri)))];
    const reads = [];
    const startRead = (index) => {
        const read = fetchBytes(urls[index], ctx);
        // Marked handled as it starts, as in `startPlaylistReads`: once an
        // earlier read has thrown, a later one's turn never comes.
        void read.catch(() => undefined);
        reads[index] = read;
    };
    for (let i = 0; i < Math.min(VTT_READS_IN_FLIGHT, urls.length); i++) {
        startRead(i);
    }
    const replacements = new Map();
    for (let i = 0; i < urls.length; i++) {
        const bytes = await reads[i];
        if (i + VTT_READS_IN_FLIGHT < urls.length) {
            startRead(i + VTT_READS_IN_FLIGHT);
        }
        if (!isEncryptedPayload(bytes))
            continue;
        const absolute = urls[i];
        const asset = await decodeMaybeEncrypted(bytes, absolute, {
            keyHex: ctx.keyHex,
            expect: 'vtt',
            subtle: ctx.subtle,
        });
        replacements.set(absolute, ctx.serveStrategy.serve(asset.text, VTT_CONTENT_TYPE));
    }
    return replacements;
}
function requireKeyFor(playlist, url, ctx) {
    if (ctx.keyHex || !playlist.hasAes128Key)
        return;
    throw new PipelineError('key-required', `${url} declares AES-128 segments but no session key was supplied`, { url });
}
export function masterTrackId(prefix, groupId, name, language) {
    return `${prefix}:${groupId ?? ''}:${name ?? language ?? ''}`;
}
/** One entry per language and name: a master that repeats its audio in each group lists a track once. */
export function dedupeByName(items) {
    const seen = new Set();
    return items.filter((item) => {
        const key = `${item.lang ?? ''}|${item.label}`;
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
}
function dedupeById(items) {
    const seen = new Set();
    return items.filter((item) => {
        if (seen.has(item.id))
            return false;
        seen.add(item.id);
        return true;
    });
}
//# sourceMappingURL=pipeline.js.map